/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React, { useEffect, useState } from "react";

import ContentLoader from "./components/ContentLoader";
import TabsArrowButton from "./components/TabsArrowButton";

import useCompaniesTabDetails from "../../hooks/useCompaniesTabDetails";

import DetailsContainer from "../../../DetailsContainer/DetailsContainer";

import { useStyles } from "../../../../styles";
import { COMPANY_DETAILS_TABS } from "../../../../types";

import TabsCustom from "../../../../../TabsCustom/TabsCustom";
import TabCustomPanel from "../../../../../TabsCustom/components/TabCustomPanel";

import { ApiResources } from "../../../../../../api/resources";
import RectangleLoader from "../../../../../../shared/components/loaders/RectangleLoader";
import { useIsFutureTime, useMediaQueries } from "../../../../../../shared";
import { findIndexOfArrayElement } from "../../../../../../shared/functions/functions";
import { endOfBetaVersionDate } from "../../../../../../Pages/Auth/constants";
import { useAuth } from "../../../../../../context/Auth/AuthProvider";
import { USER_PERMISSIONS } from "../../../../../../roles/types/enums";

interface CompaniesDetailsContainerProps {
  details: React.ReactNode;
  fileSyncContent: React.ReactNode;
  emailTransferContent: (companyId: string) => React.JSX.Element | null;
  contentHeight: string;
  companyId: string;
  displayInitialLoader: boolean;
}

const CompaniesDetailsContainer = ({
  details,
  fileSyncContent,
  emailTransferContent,
  contentHeight,
  companyId,
  displayInitialLoader,
}: CompaniesDetailsContainerProps) => {
  const { getAccessStatus } = useAuth();
  const { getTabDetailsData, isLoading } = useCompaniesTabDetails();
  const { toMd, toLg } = useMediaQueries();
  const { isFutureTime } = useIsFutureTime(endOfBetaVersionDate);

  const [value, setValue] = useState(0);

  const { customTabsContainer } = useStyles();

  const desktopTabsCustomWidth = toLg ? "309px" : "419px"; // desktop details width: toLg ? "361px" : "472px" - 52px of left and right padding;

  const tabsArrayUntilBetaEnd = [
    COMPANY_DETAILS_TABS.Details,
    COMPANY_DETAILS_TABS.FileSync,
    COMPANY_DETAILS_TABS.EmailTransfer,
  ];

  const tabsArrayAfterBetaEnd = [
    COMPANY_DETAILS_TABS.Details,
    COMPANY_DETAILS_TABS.EmailTransfer,
  ];

  const tabsArray: string[] = isFutureTime
    ? tabsArrayUntilBetaEnd
    : tabsArrayAfterBetaEnd;

  const detailsTabIndex = findIndexOfArrayElement(
    tabsArray,
    COMPANY_DETAILS_TABS.Details
  );

  const emailTransferTabIndex = findIndexOfArrayElement(
    tabsArray,
    COMPANY_DETAILS_TABS.EmailTransfer
  );

  const fileSyncTabIndex = findIndexOfArrayElement(
    tabsArrayUntilBetaEnd,
    COMPANY_DETAILS_TABS.FileSync
  );

  useEffect(() => {
    value === emailTransferTabIndex &&
      getTabDetailsData(companyId, ApiResources.EmailRecipients);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, companyId]);

  const isDataFetchLoading = isLoading;

  const handleRightArrowClick = () => {
    if (value < tabsArray.length - 1) {
      setValue(value + 1);
    }
  };

  const handleLeftArrowClick = () => {
    if (value > 0) {
      setValue(value - 1);
    }
  };

  return (
    <>
      {/* Tab loader */}
      {displayInitialLoader ? (
        <RectangleLoader
          height={"32px"}
          customStyle={{ margin: "16px 28px 0 24px" }}
        />
      ) : (
        <div data-testid="custom-tabs-container" css={css(customTabsContainer)}>
          <TabsArrowButton
            onButtonClick={handleLeftArrowClick}
            isDisabled={value === 0}
            leftButton
          />

          <TabsCustom
            setValue={setValue}
            value={value}
            tabsArray={tabsArray}
            // in this case style here does not accept dynamic values and affects the tabs to move to the very last tab on first render
            customStyle={{
              width: toMd ? "calc(100% - 52px)" : desktopTabsCustomWidth,
            }}
            hasAccess={getAccessStatus(USER_PERMISSIONS.ManageCompanies)}
          />
          <TabsArrowButton
            onButtonClick={handleRightArrowClick}
            isDisabled={
              value === tabsArray.length - 1 ||
              !getAccessStatus(USER_PERMISSIONS.ManageCompanies)
            }
          />
        </div>
      )}

      {isDataFetchLoading || displayInitialLoader ? (
        <ContentLoader contentHeight={contentHeight} isLoading={isLoading} />
      ) : (
        <>
          <TabCustomPanel value={value} index={detailsTabIndex}>
            <DetailsContainer contentHeight={contentHeight}>
              {details}
            </DetailsContainer>
          </TabCustomPanel>

          {isFutureTime && (
            <TabCustomPanel value={value} index={fileSyncTabIndex}>
              <DetailsContainer contentHeight={contentHeight}>
                {fileSyncContent}
              </DetailsContainer>
            </TabCustomPanel>
          )}

          <TabCustomPanel value={value} index={emailTransferTabIndex}>
            <DetailsContainer contentHeight={contentHeight}>
              {emailTransferContent(companyId)}
            </DetailsContainer>
          </TabCustomPanel>
        </>
      )}
    </>
  );
};

export default CompaniesDetailsContainer;
